import React, { useState, useEffect } from 'react';
import { Bell, Menu, Moon, Sun } from 'lucide-react';
import OccultLogo from './Logo';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

// const BASE_API_URL = "https://altisonant.xyz/api"
const BASE_API_URL = "http://localhost:8080"

interface TemperatureDataPoint {
  timestamp: number;
  VC: number | null;
  NOAA: number | null;
  NWS: number | null;
  ALTI: number | null;
  ALTI_PROB_UPPER: number | null;
  ALTI_PROB_LOWER: number | null;
  ALTI_PROB_STD: number | null;
  OBS: number | null;  // Add this new field
}

interface LastUpdatedData {
  VC: string;
  NOAA: string;
  NWS: string;
  ALTI: string;
  ALTI_PROB: string;
  OBS: string;  // Add this new field
}

interface WeatherCityData {
  [city: string]: TemperatureDataPoint[];
}

interface StationMapData {
  [city: string]: string;
}

interface TimezoneData {
  [city: string]: string;
}

interface LastUpdatedCityData {
  [city: string]: LastUpdatedData;
}

// Visual Crossing Forecast interface
interface VcForecast {
  datetime: string;
  datetimeEpoch: number;
  temp: number;
  feelslike: number;
  humidity: number;
  dew: number;
  precip: number;
  precipprob: number;
  snow: number;
  snowdepth: number;
  preciptype?: string[];
  windgust: number;
  windspeed: number;
  winddir: number;
  pressure: number;
  visibility: number;
  cloudcover: number;
  solarradiation: number;
  solarenergy: number;
  uvindex: number;
  severerisk: number;
  conditions: string;
  icon: string;
  stations?: string[];
  source: string;
  date: string;
  retrieved_at_ms: number;
}

// ALTI Forecast interface
interface AltiForecast {
  station: string;
  basis_time: string;
  target_time: string;
  local_basis_time: string;
  local_target_time: string;
  date: string;
  fcst_t: number;       // Surface temperature (0m) - Maps to NOAA-0m (NWS)
  fcst_t2m: number;     // 2-meter temperature - Maps to NOAA-2m (NOAA)
  predicted_t: number;  // ALTI's prediction
  predicted_std?: number;
  prediction_time: string;
  timezone: string;
}

interface ObservationData {
  date_time: string;
  air_temp_set_1: number;
  relative_humidity_set_1: number;
  wind_speed_set_1: number;
  wind_direction_set_1: number;
  altimeter_set_1: number;
  visibility_set_1: number;
  metar_set_1: string;
  cloud_layer_1_code_set_1: number;
  pressure_set_1d: number;
  sea_level_pressure_set_1d: number;
  wet_bulb_temp_set_1d: number;
  wind_cardinal_direction_set_1d: string;
  weather_summary_set_1d: string;
  dew_point_temperature_set_1d: number;
  heat_index_set_1d: number;
  local_obs_time: string;
  local_obs_date: string;
  retrieval_time: string;
}

// Station mapping for API calls
const stationMap: StationMapData = {
  Chicago: 'KMDW',
  Austin: 'KAUS',
  NYC: 'KNYC',
  Miami: 'KMIA',
  Denver: 'KDEN',
  LA: 'KLAX'
};

// Define timezone information for each city
const cityTimezones: TimezoneData = {
  Chicago: 'America/Chicago',      // Central Time
  Austin: 'America/Chicago',       // Central Time
  NYC: 'America/New_York',         // Eastern Time
  Miami: 'America/New_York',       // Eastern Time
  Denver: 'America/Denver',        // Mountain Time
  LA: 'America/Los_Angeles'        // Pacific Time
};

// Helper function to convert UTC to local time for a city
function convertToLocalTime(utcDate: Date, city: string): Date {
  // Create a date string in ISO format
  const dateStr = utcDate.toISOString();

  // Get the timezone for the city
  const timezone = cityTimezones[city] || 'UTC';

  // Convert to local time using the Intl.DateTimeFormat API
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });

  // Format the date to a string in the local timezone
  const localDateParts = formatter.formatToParts(utcDate);

  // Extract components from the formatted parts
  const year = parseInt(localDateParts.find(part => part.type === 'year')?.value || '0');
  const month = parseInt(localDateParts.find(part => part.type === 'month')?.value || '0') - 1; // Months are 0-indexed in JS Date
  const day = parseInt(localDateParts.find(part => part.type === 'day')?.value || '0');
  const hour = parseInt(localDateParts.find(part => part.type === 'hour')?.value || '0');
  const minute = parseInt(localDateParts.find(part => part.type === 'minute')?.value || '0');
  const second = parseInt(localDateParts.find(part => part.type === 'second')?.value || '0');

  // Create a new date using the local components
  return new Date(year, month, day, hour, minute, second);
}

// Function to parse ISO 8601 datetime with timezone
function parseISOWithTimezone(dateTimeString: string): Date {
  // For strings like "2025-04-13T15:00-05:00[America/Chicago]"
  // We need to remove the timezone identifier in brackets for browser compatibility
  const cleanDateStr = dateTimeString.replace(/\[.*\]$/, '');
  return new Date(cleanDateStr);
}


// Initialize with placeholder data then fetch real data
const weatherData: WeatherCityData = {
  Chicago: [],
  Austin: [],
  NYC: [],
  Miami: [],
  Denver: [],
  LA: [],
};

// Initialize last updated timestamps
const lastUpdated: LastUpdatedCityData = {
  Chicago: {
    VC: 'Loading...',
    NOAA: 'Loading...',
    NWS: 'Loading...',
    ALTI: 'Loading...',
    ALTI_PROB: 'Loading...',
    OBS: 'Loading...'
  },
  Austin: {
    VC: 'Loading...',
    NOAA: 'Loading...',
    NWS: 'Loading...',
    ALTI: 'Loading...',
    ALTI_PROB: 'Loading...',
    OBS: 'Loading...'
  },
  NYC: {
    VC: 'Loading...',
    NOAA: 'Loading...',
    NWS: 'Loading...',
    ALTI: 'Loading...',
    ALTI_PROB: 'Loading...',
    OBS: 'Loading...'
  },
  Miami: {
    VC: 'Loading...',
    NOAA: 'Loading...',
    NWS: 'Loading...',
    ALTI: 'Loading...',
    ALTI_PROB: 'Loading...',
    OBS: 'Loading...'
  },
  Denver: {
    VC: 'Loading...',
    NOAA: 'Loading...',
    NWS: 'Loading...',
    ALTI: 'Loading...',
    ALTI_PROB: 'Loading...',
    OBS: 'Loading...'
  },
  LA: {
    VC: 'Loading...',
    NOAA: 'Loading...',
    NWS: 'Loading...',
    ALTI: 'Loading...',
    ALTI_PROB: 'Loading...',
    OBS: 'Loading...'
  }
};

// Format the data for ApexCharts
function formatWeatherChartData(cityData: TemperatureDataPoint[], city: string) {
  // Create series for each data source
  const series = [
    {
      name: 'VC',
      data: cityData.map(item => ({
        x: item.timestamp,
        y: item.VC
      }))
    },
    {
      name: 'NOAA-2m',
      data: cityData.map(item => ({
        x: item.timestamp,
        y: item.NOAA
      }))
    },
    // {
    //   name: 'NOAA-0m',
    //   data: cityData.map(item => ({
    //     x: item.timestamp,
    //     y: item.NWS
    //   }))
    // },
    {
      name: 'ALTI',
      data: cityData.map(item => ({
        x: item.timestamp,
        y: item.ALTI
      }))
    },
    {
      name: 'OBS',  // Add this new series
      data: cityData.map(item => ({
        x: item.timestamp,
        y: item.OBS
      }))
    }
    // {
    //   name: 'ALTI Probability Range',
    //   data: cityData.map(item => ({
    //     x: item.timestamp,
    //     y: item.ALTI,
    //     lower: item.ALTI_PROB_LOWER,
    //     upper: item.ALTI_PROB_UPPER,
    //     std: item.ALTI_PROB_STD
    //   })),
    //   type: 'rangeArea',
    //   fillColor: '#EF444440',  // ALTI color with 40% opacity
    // }
  ];

  // If any probability data exists, add an area range series
  if (cityData.some(item => item.ALTI_PROB_LOWER !== null && item.ALTI_PROB_UPPER !== null)) {
    series.push({
      name: 'ALTI Probability',
      type: 'rangeArea',
      data: cityData
        .filter(item => item.ALTI_PROB_LOWER !== null && item.ALTI_PROB_UPPER !== null)
        .map(item => ({
          x: item.timestamp,
          y: [item.ALTI_PROB_LOWER, item.ALTI_PROB_UPPER]  // Upper bound
        }))
    } as any);
  }

  // Get min and max timestamps for the x-axis range (in local time)
  const now = new Date();
  const localToday = convertToLocalTime(now, city);
  localToday.setHours(0, 0, 0, 0); // Start of today in local time

  const localTomorrow = new Date(localToday);
  localTomorrow.setDate(localTomorrow.getDate() + 1); // Start of tomorrow in local time

  const options: ApexOptions = {
    chart: {
      type: 'rangeArea',
      height: 350,
      toolbar: {
        show: false
      }
    },
    colors: [
      '#3B82F6',
      '#10B981',
      // '#F59E0B', 
      '#EF4444',
      '#8B5CF6',
      '#fb7185'
    ],
    stroke: {
      width: [2, 2, 2, 2, 0], // No stroke for the range area
      curve: 'straight',
      dashArray: [0, 0, 0, 2, 0]
    },
    fill: {
      type: ['solid', 'solid', 'solid', 'solid', 'solid'],
      opacity: [1, 1, 1, 2, 0.3],
    },
    xaxis: {
      type: 'datetime',
      min: localToday.getTime(),
      max: localTomorrow.getTime() - 1, // End of today
      labels: {
        format: 'HH:mm', // Show hours and minutes for 24-hour view
        datetimeUTC: false // This is important for local time display
      },
      tickAmount: 6, // Show fewer ticks for cleaner display
      title: {
        text: `Local Time (${cityTimezones[city]?.split('/')[1]?.replace('_', ' ') || city})`
      }
    },
    yaxis: {
      title: {
        text: 'Temperature (°F)'
      }
    },
    legend: {
      position: 'top'
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false, // Important to prevent hover issues
      x: {
        format: 'HH:mm'
      },
      // Remove the custom function and use the standard formatter
      y: {
        formatter: function (value, { seriesIndex, dataPointIndex, w }) {
          if (value === null || value === undefined) return 'N/A';

          // For ALTI series, add probability info if available
          if (seriesIndex === 2) { // ALTI series
            const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
            if (dataPoint && dataPoint.std) {
              return `${value.toFixed(1)}°F (±${dataPoint.std.toFixed(1)}°F)`;
            }
          }

          return `${value.toFixed(1)}°F`;
        }
      }
    }
  };

  return { series, options };
}

// Function to calculate maximum temperature for each data source
function calculateMaxTemperatures(data: TemperatureDataPoint[]) {
  const maxTemps = {
    VC: null as number | null,
    NOAA: null as number | null,
    NWS: null as number | null,
    ALTI: null as number | null,
    ALTI_PROB_UPPER: null as number | null,
    ALTI_PROB_LOWER: null as number | null,
    OBS: null as number | null
  };

  // Initialize with very low values
  let maxVC = -Infinity;
  let maxNOAA = -Infinity;
  let maxNWS = -Infinity;
  let maxALTI = -Infinity;
  let maxALTI_PROB_UPPER = -Infinity;
  let maxALTI_PROB_LOWER = -Infinity;
  let maxOBS = -Infinity


  // Check if any valid temperatures exist
  let hasVC = false;
  let hasNOAA = false;
  let hasNWS = false;
  let hasALTI = false;
  let hasALTI_PROB_UPPER = false;
  let hasALTI_PROB_LOWER = false;
  let hasOBS = false;

  // Find maximum for each source
  data.forEach(item => {
    if (item.VC !== null && !isNaN(item.VC)) {
      maxVC = Math.max(maxVC, item.VC);
      hasVC = true;
    }
    if (item.NOAA !== null && !isNaN(item.NOAA)) {
      maxNOAA = Math.max(maxNOAA, item.NOAA);
      hasNOAA = true;
    }
    if (item.NWS !== null && !isNaN(item.NWS)) {
      maxNWS = Math.max(maxNWS, item.NWS);
      hasNWS = true;
    }
    if (item.ALTI !== null && !isNaN(item.ALTI)) {
      maxALTI = Math.max(maxALTI, item.ALTI);
      hasALTI = true;
    }
    if (item.OBS !== null && !isNaN(item.OBS)) {
      maxOBS = Math.max(maxOBS, item.OBS);
      hasOBS = true;
    }
    if (item.ALTI_PROB_UPPER !== null && !isNaN(item.ALTI_PROB_UPPER)) {
      maxALTI_PROB_UPPER = Math.max(maxALTI_PROB_UPPER, item.ALTI_PROB_UPPER);
      hasALTI_PROB_UPPER = true;
    }
    if (item.ALTI_PROB_LOWER !== null && !isNaN(item.ALTI_PROB_LOWER)) {
      maxALTI_PROB_LOWER = Math.max(maxALTI_PROB_LOWER, item.ALTI_PROB_LOWER);
      hasALTI_PROB_LOWER = true;
    }
  });

  // Set values or null if no valid data
  maxTemps.VC = hasVC ? maxVC : null;
  maxTemps.NOAA = hasNOAA ? maxNOAA : null;
  maxTemps.NWS = hasNWS ? maxNWS : null;
  maxTemps.ALTI = hasALTI ? maxALTI : null;
  maxTemps.ALTI_PROB_UPPER = hasALTI_PROB_UPPER ? maxALTI_PROB_UPPER : null;
  maxTemps.ALTI_PROB_LOWER = hasALTI_PROB_LOWER ? maxALTI_PROB_LOWER : null;

  return maxTemps;
}

// Weather Panel Component
interface WeatherPanelProps {
  city: string;
  data: TemperatureDataPoint[];
  lastUpdated: LastUpdatedData;
}

const WeatherPanel: React.FC<WeatherPanelProps> = ({ city, data, lastUpdated }) => {
  const chartData = formatWeatherChartData(data, city);
  const station = stationMap[city] || '';
  const latestData = data && data.length > 0 ? data[data.length - 1] : { ALTI: 0 };
  const timezone = cityTimezones[city]?.split('/')[1]?.replace('_', ' ') || city;

  // Calculate max temperatures
  const maxTemps = calculateMaxTemperatures(data);

  let avgStdDev: number | null = null;
  let stdDevCount = 0;
  data.forEach(item => {
    if (item.ALTI_PROB_STD !== null && !isNaN(item.ALTI_PROB_STD)) {
      avgStdDev = (avgStdDev || 0) + item.ALTI_PROB_STD;
      stdDevCount++;
    }
  });
  if (avgStdDev !== null && stdDevCount > 0) {
    avgStdDev = avgStdDev / stdDevCount;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-xl font-semibold">{city}</h3>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Station: {station} ({timezone})
          </div>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Current: {latestData.ALTI}°F
        </div>
      </div>

      <div className="h-64">
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="line"
          height="100%"
        />
      </div>

      <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 grid grid-cols-2 gap-2">
        <div className="flex items-center">
          <span className="w-3 h-3 inline-block mr-1 rounded-full" style={{ backgroundColor: '#3B82F6' }}></span>
          <div className="flex flex-col">
            <span>VC: {lastUpdated.VC}</span>
            <span className="font-medium">Max: {maxTemps.VC !== null ? `${maxTemps.VC.toFixed(1)}°F` : 'N/A'}</span>
          </div>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 inline-block mr-1 rounded-full" style={{ backgroundColor: '#10B981' }}></span>
          <div className="flex flex-col">
            <span>NOAA-2m: {lastUpdated.NOAA}</span>
            <span className="font-medium">Max: {maxTemps.NOAA !== null ? `${maxTemps.NOAA.toFixed(1)}°F` : 'N/A'}</span>
          </div>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 inline-block mr-1 rounded-full" style={{ backgroundColor: '#F59E0B' }}></span>
          <div className="flex flex-col">
            <span>NOAA-0m: {lastUpdated.NWS}</span>
            <span className="font-medium">Max: {maxTemps.NWS !== null ? `${maxTemps.NWS.toFixed(1)}°F` : 'N/A'}</span>
          </div>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 inline-block mr-1 rounded-full" style={{ backgroundColor: '#EF4444' }}></span>
          <div className="flex flex-col">
            <span>ALTI: {lastUpdated.ALTI}</span>
            <span className="font-medium">Max: {maxTemps.ALTI !== null ? `${maxTemps.ALTI.toFixed(1)}°F` : 'N/A'}</span>
          </div>
        </div>
        <div className="flex items-center col-span-2">
          <span className="w-3 h-3 inline-block mr-1 rounded-full bg-gradient-to-r from-rose-400 to-rose-200"></span>
          <div className="flex flex-col">
            <span>ALTI Probability: {lastUpdated.ALTI_PROB}</span>
            <span className="font-medium">
              Avg Uncertainty: {avgStdDev !== null ? `±${avgStdDev.toFixed(2)}°F` : 'N/A'}
              {maxTemps.ALTI_PROB_UPPER !== null && maxTemps.ALTI_PROB_LOWER !== null ?
                ` (Max Low-High: ${maxTemps.ALTI_PROB_LOWER.toFixed(1)}-${maxTemps.ALTI_PROB_UPPER.toFixed(1)}°F)` : ''}
            </span>
          </div>
        </div>
        <div className="flex items-center">
          <span className="w-3 h-3 inline-block mr-1 rounded-full" style={{ backgroundColor: '#8B5CF6' }}></span>
          <div className="flex flex-col">
            <span>OBS: {lastUpdated.OBS}</span>
            <span className="font-medium">Max: {maxTemps.OBS !== null ? `${maxTemps.OBS.toFixed(1)}°F` : 'N/A'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface WeatherPageParams {
  date: string
}

// Main Weather component
const Weather: React.FC<WeatherPageParams> = ({ date }) => {
  // State for weather data and last updated timestamps
  const [data, setData] = useState<WeatherCityData>(weatherData);
  const [updatedTimestamps, setUpdatedTimestamps] = useState<LastUpdatedCityData>(lastUpdated);
  const [loading, setLoading] = useState<{ [city: string]: boolean }>({
    Chicago: false,
    Austin: false,
    NYC: false,
    Miami: false,
    Denver: false,
    LA: false
  });
  const [error, setError] = useState<{ [city: string]: string | null }>({
    Chicago: null,
    Austin: null,
    NYC: null,
    Miami: null,
    Denver: null,
    LA: null
  });

  // Function to fetch live data for a city
  const fetchWeatherData = async (city: string) => {
    const station = stationMap[city];
    if (!station) return;

    try {
      setLoading(prev => ({ ...prev, [city]: true }));
      setError(prev => ({ ...prev, [city]: null }));

      // Create an array to store the combined hourly data
      const hourlyData: TemperatureDataPoint[] = [];

      // Get today in local time for the city
      const localToday = convertToLocalTime(new Date(), city);
      localToday.setHours(0, 0, 0, 0); // Start of today in local time

      // Initialize hourlyData with placeholder values for each hour
      for (let hour = 0; hour < 24; hour++) {
        const localHourDate = new Date(localToday);
        localHourDate.setHours(hour);

        // Start with existing data if available or generate placeholder
        const existingData = data[city].find(d => {
          const itemDate = new Date(d.timestamp);
          return itemDate.getHours() === hour;
        });

        if (existingData) {
          hourlyData.push(existingData);
        }
        else {
          hourlyData.push({
            timestamp: localHourDate.getTime(),
            VC: null,
            NOAA: null,
            NWS: null,
            ALTI: null,
            ALTI_PROB_UPPER: null,
            ALTI_PROB_LOWER: null,
            ALTI_PROB_STD: null,
            OBS: null
          });
        }
      }

      // 1. Fetch VC data
      try {
        const vcResponse = await fetch(`${BASE_API_URL}/live/forecast/${station}/${date}`);

        if (vcResponse.ok) {
          const vcForecasts: VcForecast[] = await vcResponse.json();

          if (vcForecasts && vcForecasts.length > 0) {
            // Update last updated timestamp for VC
            const lastUpdatedVC = new Date(Math.max(...vcForecasts.map(f => f.retrieved_at_ms))).toLocaleString();
            setUpdatedTimestamps(prev => ({
              ...prev,
              [city]: {
                ...prev[city],
                VC: lastUpdatedVC
              }
            }));

            // Update VC temperatures in hourlyData
            vcForecasts.forEach(forecast => {
              const forecastHour = parseInt(forecast.datetime.split(':')[0]);
              const hourIndex = hourlyData.findIndex(item => {
                const itemDate = new Date(item.timestamp);
                return itemDate.getHours() === forecastHour;
              });

              if (hourIndex !== -1) {
                hourlyData[hourIndex] = {
                  ...hourlyData[hourIndex],
                  VC: forecast.temp
                };
              }
            });
          }
        }
      } catch (vcErr) {
        console.error(`Error fetching VC data for ${city}:`, vcErr);
        // Continue with ALTI data fetch even if VC fails
      }

      // 2. Fetch ALTI data (which includes NOAA-0m and NOAA-2m)
      try {
        const altiResponse = await fetch(`${BASE_API_URL}/live/alti_fcst/${station}/${date}`);

        if (altiResponse.ok) {
          const altiForecasts: AltiForecast[] = await altiResponse.json();

          if (altiForecasts && altiForecasts.length > 0) {
            // Update last updated timestamps for ALTI, NOAA-0m, and NOAA-2m
            const predictionTimes = altiForecasts.map(f =>
              new Date(parseISOWithTimezone(f.prediction_time)).getTime()
            );
            const lastUpdatedTime = new Date(Math.max(...predictionTimes)).toLocaleString();

            setUpdatedTimestamps(prev => ({
              ...prev,
              [city]: {
                ...prev[city],
                ALTI: lastUpdatedTime,
                NOAA: lastUpdatedTime, // NOAA-2m
                NWS: lastUpdatedTime,  // NOAA-0m
              }
            }));

            // Process ALTI forecasts and update temperatures
            altiForecasts.forEach(forecast => {
              const localTargetTime = parseISOWithTimezone(forecast.local_target_time);
              const hour = localTargetTime.getHours();

              const hourIndex = hourlyData.findIndex(item => {
                const itemDate = new Date(item.timestamp);
                return itemDate.getHours() === hour;
              });

              if (hourIndex !== -1) {
                hourlyData[hourIndex] = {
                  ...hourlyData[hourIndex],
                  ALTI: Number(forecast.predicted_t.toFixed(2)),
                  NWS: Number(forecast.fcst_t.toFixed(2)),      // NOAA-0m (surface temperature)
                  NOAA: Number(forecast.fcst_t2m.toFixed(2)),   // NOAA-2m (2-meter temperature)
                };
              }
            });
          }
        }
      } catch (altiErr) {
        console.error(`Error fetching ALTI data for ${city}:`, altiErr);
        // Continue even if ALTI fetch fails
      }

      // Add this after the existing ALTI data fetch
      // 3. Fetch ALTI probability forecast data
      try {
        const altiProbResponse = await fetch(`${BASE_API_URL}/live/alti_prob_fcst/${station}/${date}`);

        if (altiProbResponse.ok) {
          const altiProbForecasts: AltiForecast[] = await altiProbResponse.json();

          if (altiProbForecasts && altiProbForecasts.length > 0) {
            // Update last updated timestamp for ALTI probability forecast
            const predictionTimes = altiProbForecasts.map(f =>
              new Date(parseISOWithTimezone(f.prediction_time)).getTime()
            );
            const lastUpdatedTime = new Date(Math.max(...predictionTimes)).toLocaleString();

            setUpdatedTimestamps(prev => ({
              ...prev,
              [city]: {
                ...prev[city],
                ALTI_PROB: lastUpdatedTime
              }
            }));

            // Process ALTI probability forecasts and update probability bounds
            altiProbForecasts.forEach(forecast => {
              const localTargetTime = parseISOWithTimezone(forecast.local_target_time);
              const hour = localTargetTime.getHours();

              const hourIndex = hourlyData.findIndex(item => {
                const itemDate = new Date(item.timestamp);
                return itemDate.getHours() === hour;
              });

              if (hourIndex !== -1 && forecast.predicted_std !== undefined) {
                // Calculate the upper and lower bounds for the probability distribution
                // Using 1 standard deviation (68% confidence interval)
                const upperBound = forecast.predicted_t + 0.5 * forecast.predicted_std;
                const lowerBound = forecast.predicted_t - 0.5 * forecast.predicted_std;

                hourlyData[hourIndex] = {
                  ...hourlyData[hourIndex],
                  ALTI_PROB_UPPER: Number(upperBound.toFixed(2)),
                  ALTI_PROB_LOWER: Number(lowerBound.toFixed(2)),
                  ALTI_PROB_STD: Number(forecast.predicted_std.toFixed(2))
                };
              }
            });
          }
        }
      } catch (altiProbErr) {
        console.error(`Error fetching ALTI probability data for ${city}:`, altiProbErr);
        // Continue even if ALTI probability fetch fails
      }

      try {
        const obsResponse = await fetch(`${BASE_API_URL}/live/obs/${station}/${date}`);

        if (obsResponse.ok) {
          const observations: ObservationData[] = await obsResponse.json();

          if (observations && observations.length > 0) {
            // Update last updated timestamp for OBS
            const retrievalTimes = observations.map(obs =>
              new Date(obs.retrieval_time).getTime()
            );
            const lastUpdatedTime = new Date(Math.max(...retrievalTimes)).toLocaleString();

            setUpdatedTimestamps(prev => ({
              ...prev,
              [city]: {
                ...prev[city],
                OBS: lastUpdatedTime
              }
            }));

            // Process observation data and update temperatures
            observations.forEach(obs => {
              // Parse the observation time
              const obsTime = new Date(+parseISOWithTimezone(obs.local_obs_time) + 6 * 3600 * 1000);
              
              const hour = obsTime.getHours();
              
              console.log(obs.local_obs_time, obsTime, hour)

              const hourIndex = hourlyData.findIndex(item => {
                const itemDate = new Date(item.timestamp);
                const hr = itemDate.getHours() === hour;
                return hr
              });

              if (hourIndex !== -1 && obs.air_temp_set_1 !== undefined) {
                hourlyData[hourIndex] = {
                  ...hourlyData[hourIndex],
                  OBS: Number(obs.air_temp_set_1.toFixed(2))
                };
              }
            });
          }
        }
      } catch (obsErr) {
        console.error(`Error fetching observation data for ${city}:`, obsErr);
        // Continue even if observation fetch fails
      }

      // Sort by timestamp and update state
      hourlyData.sort((a, b) => a.timestamp - b.timestamp);

      setData(prev => ({
        ...prev,
        [city]: hourlyData
      }));

      setLoading(prev => ({ ...prev, [city]: false }));
    } catch (err) {
      console.error(`Error fetching weather data for ${city}:`, err);
      setError(prev => ({
        ...prev,
        [city]: err instanceof Error ? err.message : 'Unknown error'
      }));
      setLoading(prev => ({ ...prev, [city]: false }));
    }
  };

  // Fetch data for all cities on component mount
  useEffect(() => {
    const cities = Object.keys(stationMap);
    cities.forEach(city => {
      fetchWeatherData(city);
    }, [date]);

    // Set up polling for updates every 15 minutes
    const interval = setInterval(() => {
      cities.forEach(city => {
        fetchWeatherData(city);
      });
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Object.keys(stationMap).map(city => (
        <div key={city} className="relative">
          <WeatherPanel
            city={city}
            data={data[city]}
            lastUpdated={updatedTimestamps[city]}
          />
          {loading[city] && (
            <div className="absolute top-2 right-2">
              <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          {error[city] && (
            <div className="mt-2 text-xs text-red-500">
              Error fetching weather data: {error[city]}
              <button
                onClick={() => fetchWeatherData(city)}
                className="ml-2 text-indigo-500 hover:text-indigo-700"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Sports Component (placeholder)
const Sports: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold mb-4">Sports Data Coming Soon</h3>
      <p className="text-gray-600 dark:text-gray-400">
        This section will contain sports statistics and analysis.
      </p>
    </div>
  );
};

// Main App Component
const AltisonantApp: React.FC = () => {
  // Dark mode state
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    const savedMode = localStorage.getItem('darkMode');
    if (savedMode !== null) {
      return savedMode === 'true';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Current date for API calls
  const [currentDate, setCurrentDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD format
  });

  // Active tab state - now just Weather or Sports
  const [activeTab, setActiveTab] = useState<'weather' | 'sports'>('weather');

  // Dark mode toggle
  const toggleDarkMode = (): void => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('darkMode', newMode.toString());
  };

  // Apply dark class to document when darkMode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Handle date change
  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentDate(event.target.value);
  };

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'weather':
        return <Weather date={currentDate} />;
      case 'sports':
        return <Sports />;
      default:
        return <Weather date={currentDate} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-md">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              {/* Logo and smaller title underneath */}
              <div className="flex items-center justify-center">
                <OccultLogo darkMode={darkMode} />
                <h1 className="text-2xl px-3 font-semibold font-sans mt-1">ALTISONANT</h1>
              </div>
              {/* <h1 className="text-2xl font-normal font-sans mt-1">ALTISONANT</h1> */}
            </div>

            <div className="flex items-center">
              <input
                type="date"
                value={currentDate}
                onChange={handleDateChange}
                className="bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2 text-sm mr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />

              <button className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 relative mr-2">
                <Bell size={20} />
                <span className="absolute top-1 right-1 bg-red-500 rounded-full w-2 h-2"></span>
              </button>

              <button
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 mr-2"
                onClick={toggleDarkMode}
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              <button className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {/* New Tab Selector: Weather or Sports */}
        <div className="flex mb-6 border-b border-gray-300 dark:border-gray-700">
          <button
            className={`py-2 px-4 ${activeTab === 'weather'
              ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400'
              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('weather')}
          >
            Weather
          </button>
          <button
            className={`py-2 px-4 ${activeTab === 'sports'
              ? 'border-b-2 border-indigo-600 text-indigo-600 dark:text-indigo-400'
              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
            onClick={() => setActiveTab('sports')}
          >
            Sports
          </button>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </main>
    </div>
  );
};

export default AltisonantApp;